import React from "react";
import {
  Box,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  FormControl,
  Button,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";

const StrategyPerformanceFilters = ({
  strategyList,
  strategyVal,
  setStrategyVal,
  period,
  setPeriod,
  customFromDate,
  setCustomFromDate,
  customToDate,
  setCustomToDate,
  appliedCustomFromDate,
  setAppliedCustomFromDate,
  appliedCustomToDate,
  setAppliedCustomToDate,
  dateRangeLimits,
}) => {
  const theme = useTheme();

  const handleSubmit = () => {
    // Validate dates are within range
    if (dateRangeLimits && customFromDate && customToDate) {
      if (
        customFromDate < dateRangeLimits.minDate ||
        customFromDate > dateRangeLimits.maxDate
      ) {
        toast.error(
          `From date must be between ${dateRangeLimits.minDate} and ${dateRangeLimits.maxDate}`
        );
        return;
      }
      if (
        customToDate < dateRangeLimits.minDate ||
        customToDate > dateRangeLimits.maxDate
      ) {
        toast.error(
          `To date must be between ${dateRangeLimits.minDate} and ${dateRangeLimits.maxDate}`
        );
        return;
      }
      if (customFromDate > customToDate) {
        toast.error("From date must be before To date");
        return;
      }
    }
    // Apply the dates to trigger filtering
    setAppliedCustomFromDate(customFromDate);
    setAppliedCustomToDate(customToDate);
  };

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          {/* Strategy Dropdown - Fixed width */}
          <Box sx={{ width: { xs: "100%", sm: "500px" }, flexShrink: 0 }}>
            <FormControl fullWidth>
              <Autocomplete
                options={strategyList || []}
                value={strategyVal || null}
                autoSelect
                isOptionEqualToValue={(option, value) => option === value}
                onChange={(e, newValue) => setStrategyVal(newValue || "")}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Strategy"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { height: "40px" },
                      "& .MuiInputLabel-root": {
                        position: "absolute",
                        top: "-9px",
                        left: "14px",
                        transform: "scale(0.75)",
                        transformOrigin: "top left",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>

          {/* Period Dropdown - Fixed width */}
          <Box sx={{ width: { xs: "100%", sm: "400px" }, flexShrink: 0 }}>
            <FormControl fullWidth>
              <InputLabel id="period-select-label">Period</InputLabel>
              <Select
                labelId="period-select-label"
                value={period}
                label="Period"
                onChange={(e) => setPeriod(e.target.value)}
                variant="outlined"
                disabled={!strategyVal}
                sx={{ height: "40px" }}
              >
                <MenuItem value="Inception to date">Inception to date</MenuItem>
                <MenuItem value="This Week">This Week</MenuItem>
                <MenuItem value="This Month">This Month</MenuItem>
                <MenuItem value="Last Month">Last Month</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Custom Date Inputs and Submit Button */}
          <Box sx={{ width: { xs: "100%", sm: "180px" }, flexShrink: 0 }}>
            <TextField
              fullWidth
              label="From"
              type="date"
              value={customFromDate}
              onChange={(e) => setCustomFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: dateRangeLimits?.minDate || undefined,
                max: dateRangeLimits?.maxDate || undefined,
              }}
              variant="outlined"
              disabled={!strategyVal || period !== "Custom"}
              sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "180px" }, flexShrink: 0 }}>
            <TextField
              fullWidth
              label="To"
              type="date"
              value={customToDate}
              onChange={(e) => setCustomToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: dateRangeLimits?.minDate || undefined,
                max: dateRangeLimits?.maxDate || undefined,
              }}
              variant="outlined"
              disabled={!strategyVal || period !== "Custom"}
              sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "120px" }, flexShrink: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleSubmit}
              disabled={
                !strategyVal ||
                period !== "Custom" ||
                !customFromDate ||
                !customToDate
              }
              sx={{ height: "40px" }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StrategyPerformanceFilters;

